import React, {
  RefObject,
  useMemo,
  useEffect,
  useCallback,
  useState,
  useRef
} from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { CardsContainer } from '../CardsLayout/styled';
import { ItemCardWrapperStyled } from '../ItemCard/styled';
import Loader from '../../Loader';

type VirtualizedCardsLayoutProps = {
  items?: React.ReactNode[];
  loading: boolean;
  onLoadMore: () => void | Promise<void>;
  isStopLoad: boolean;
  forwardedRef: RefObject<HTMLDivElement>;
  className?: string;
};

const LOAD_THROTTLE_MS = 700;
const EST_ROW_PX = 340;
const PREFETCH_MIN_PX = 600;

export const VirtualizedCardsLayoutTanstack: React.FC<
  VirtualizedCardsLayoutProps
> = ({
  items = [],
  loading,
  onLoadMore,
  isStopLoad,
  forwardedRef,
  className
}) => {
  const validatedItems = useMemo(() => {
    return items.filter(item => {
      if (item == null) return false;
      if (React.isValidElement(item)) return true;
      return typeof item === 'string' || typeof item === 'number';
    });
  }, [items]);

  const isBannerIndex = useCallback(
    (idx: number) => {
      const node = validatedItems[idx];
      return (
        React.isValidElement(node) &&
        node?.key != null &&
        String(node.key).startsWith('bottom-banner')
      );
    },
    [validatedItems]
  );

  const [containerWidth, setContainerWidth] = useState(0);
  const lastWidthRef = useRef(0);
  const roRafRef = useRef<number | null>(null);
  const rowHeightsRef = useRef(new Map<string, number>());
  const rowChildrenRef = useRef(new Map<string, HTMLElement[]>());

  useEffect(() => {
    const { current: el } = forwardedRef;
    if (!el) return;
    const init = Math.round(el.clientWidth);
    lastWidthRef.current = init;
    setContainerWidth(init);

    const ro = new ResizeObserver(entries => {
      const [entry] = entries;
      const next = Math.round(entry?.contentRect?.width ?? 0);
      if (!next) return;
      if (roRafRef.current != null) cancelAnimationFrame(roRafRef.current);
      roRafRef.current = requestAnimationFrame(() => {
        if (next !== lastWidthRef.current) {
          lastWidthRef.current = next;
          rowHeightsRef.current.clear();
          setContainerWidth(next);
        }
      });
    });
    ro.observe(el);
    return () => {
      if (roRafRef.current != null) cancelAnimationFrame(roRafRef.current);
      ro.disconnect();
    };
  }, [forwardedRef]);

  const [columns, setColumns] = useState(1);
  useEffect(() => {
    const { current: el } = forwardedRef;
    if (!el) return;
    const nodes = Array.from(
      el.querySelectorAll('[data-idx]')
    ) as HTMLElement[];
    if (nodes.length === 0) return;
    const firstNonBanner = nodes.find(n => {
      const idx = Number(n.getAttribute('data-idx'));
      return Number.isFinite(idx) && !isBannerIndex(idx);
    });
    if (!firstNonBanner) return;
    const firstTop = firstNonBanner.offsetTop;
    let cols = 0;
    for (let i = 0; i < nodes.length; i += 1) {
      const n = nodes[i];
      const idx = Number(n.getAttribute('data-idx'));
      const banner = Number.isFinite(idx) ? isBannerIndex(idx) : true;
      if (!banner && n.offsetTop === firstTop) cols += 1;
      if (!banner && n.offsetTop !== firstTop) break;
    }
    if (cols > 0) setColumns(prev => (prev !== cols ? cols : prev));
  }, [containerWidth, isBannerIndex, forwardedRef]);

  const rowRanges = useMemo(() => {
    const ranges: Array<[number, number]> = [];
    const cols = Math.max(1, columns);
    let i = 0;
    while (i < validatedItems.length) {
      if (isBannerIndex(i)) {
        ranges.push([i, i + 1]);
        i += 1;
      } else {
        const start = i;
        let taken = 0;
        while (i < validatedItems.length && taken < cols && !isBannerIndex(i)) {
          taken += 1;
          i += 1;
        }
        ranges.push([start, start + taken]);
      }
    }
    return ranges;
  }, [validatedItems, columns, isBannerIndex]);

  const overscan = useMemo(() => {
    if (columns >= 4) return 16;
    if (columns === 3) return 12;
    return 10;
  }, [columns]);

  const getRowChildren = useCallback(
    (start: number, end: number, key: string) => {
      const { current: container } = forwardedRef;
      if (!container) return [] as HTMLElement[];
      let list = rowChildrenRef.current.get(key) || [];
      const expected = end - start;
      let valid = list.length === expected;
      if (valid) {
        for (let i = 0; i < list.length; i += 1) {
          const el = list[i];
          if (
            !el ||
            !el.isConnected ||
            el.getAttribute('data-idx') !== String(start + i)
          ) {
            valid = false;
            break;
          }
        }
      }
      if (!valid) {
        const next: HTMLElement[] = [];
        for (let i = start; i < end; i += 1) {
          const child = container.querySelector(
            `[data-idx="${i}"]`
          ) as HTMLElement | null;
          if (child) next.push(child);
        }
        rowChildrenRef.current.set(key, next);
        list = next;
      }
      return list;
    },
    [forwardedRef]
  );

  const virtualizer = useWindowVirtualizer({
    count: rowRanges.length,
    estimateSize: index => {
      const range = rowRanges[index];
      const bannerRow =
        !!range && range[1] - range[0] === 1 && isBannerIndex(range[0]);
      return bannerRow ? 0 : EST_ROW_PX;
    },
    overscan,
    measureElement: el => {
      if (!el) return 0;
      const base = el.getBoundingClientRect().height;
      const rowAttr = el.getAttribute('data-row');
      if (!rowAttr) return base;
      const rowIndex = Number(rowAttr);
      const range = rowRanges[rowIndex];
      if (!range) return base;
      const [start, end] = range;
      const key = `${start}-${end}`;
      const bannerRow = end - start === 1 && isBannerIndex(start);
      const container = forwardedRef.current;
      if (!container) return base;
      let top = Number.POSITIVE_INFINITY;
      let bottom = Number.NEGATIVE_INFINITY;
      const children = getRowChildren(start, end, key);
      for (let i = 0; i < children.length; i += 1) {
        const { top: cTop, bottom: cBottom } =
          children[i].getBoundingClientRect();
        if (cTop < top) top = cTop;
        if (cBottom > bottom) bottom = cBottom;
      }
      let measured = bottom > top ? bottom - top : base;
      if (bannerRow && measured <= 1) measured = 0;
      const prev = rowHeightsRef.current.get(key);
      let nextHeight = measured;
      if (!bannerRow && prev != null && measured < prev) {
        nextHeight = prev;
      }
      if (prev == null || nextHeight !== prev)
        rowHeightsRef.current.set(key, nextHeight);
      return nextHeight;
    }
  });

  const rowKeys = useMemo(
    () => rowRanges.map(([s, e]) => `${s}-${e}`),
    [rowRanges]
  );
  useEffect(() => {
    const keep = new Set(rowKeys);
    Array.from(rowHeightsRef.current.keys()).forEach(k => {
      if (!keep.has(k)) rowHeightsRef.current.delete(k);
    });
    Array.from(rowChildrenRef.current.keys()).forEach(k => {
      if (!keep.has(k)) rowChildrenRef.current.delete(k);
    });
  }, [rowKeys]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const [firstItem] = virtualItems;
  const lastItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstItem ? firstItem.start : 0;
  const paddingBottom = lastItem ? Math.max(0, totalSize - lastItem.end) : 0;

  const measureRafByRowRef = useRef(new Map<number, number>());
  const rowElementRefMap = useRef(new Map<number, HTMLElement>());
  const makeRowMeasureRef = useCallback(
    (rowIndex: number) => (el: Element | null) => {
      const pendingId = measureRafByRowRef.current.get(rowIndex);
      if (!el || el.nodeType !== 1) {
        if (pendingId != null) cancelAnimationFrame(pendingId);
        measureRafByRowRef.current.delete(rowIndex);
        rowElementRefMap.current.delete(rowIndex);
        return;
      }
      const node = el as HTMLElement;
      if (node.getAttribute('data-index') !== String(rowIndex))
        node.setAttribute('data-index', String(rowIndex));
      if (node.getAttribute('data-row') !== String(rowIndex))
        node.setAttribute('data-row', String(rowIndex));
      const range = rowRanges[rowIndex];
      const sig = range ? `${range[0]}-${range[1]}` : '';
      if (node.getAttribute('data-row-sig') !== sig)
        node.setAttribute('data-row-sig', sig);
      rowElementRefMap.current.set(rowIndex, node);
      if (pendingId != null) cancelAnimationFrame(pendingId);
      const id = requestAnimationFrame(() => {
        const current = rowElementRefMap.current.get(rowIndex);
        if (!current || !current.isConnected) return;
        const liveRange = rowRanges[rowIndex];
        const liveSig = liveRange ? `${liveRange[0]}-${liveRange[1]}` : '';
        if (current.getAttribute('data-row-sig') !== liveSig) return;
        virtualizer.measureElement(current);
      });
      measureRafByRowRef.current.set(rowIndex, id);
    },
    [virtualizer, rowRanges]
  );

  const loadThrottleRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const { current: node } = sentinelRef;
    if (!node) return;

    let rowsFactor = 4;
    if (columns === 3) rowsFactor = 5;
    if (columns >= 4) rowsFactor = 6;
    const prefetchPx = Math.max(
      Math.round(EST_ROW_PX * rowsFactor),
      PREFETCH_MIN_PX
    );

    const io = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (!entry || !entry.isIntersecting) return;
        const now = Date.now();
        if (loading || isStopLoad || validatedItems.length === 0) return;
        if (now - loadThrottleRef.current < LOAD_THROTTLE_MS) return;
        loadThrottleRef.current = now;
        Promise.resolve(onLoadMore()).catch(() => {});
      },
      { root: null, rootMargin: `0px 0px ${prefetchPx}px 0px`, threshold: 0 }
    );

    io.observe(node);
    return () => io.disconnect();
  }, [
    loading,
    isStopLoad,
    validatedItems.length,
    onLoadMore,
    columns,
    overscan
  ]);

  useEffect(
    () => () => {
      Array.from(measureRafByRowRef.current.values()).forEach(id => {
        cancelAnimationFrame(id);
      });
      measureRafByRowRef.current.clear();
      rowElementRefMap.current.clear();
      rowChildrenRef.current.clear();
    },
    []
  );

  return (
    <CardsContainer
      ref={forwardedRef}
      className={className}
      style={{ paddingTop, paddingBottom }}
    >
      {virtualItems.map(v => {
        const { index: rowIndex } = v;
        const range = rowRanges[rowIndex];
        if (!range) return null;
        const [start, end] = range;
        return (
          <React.Fragment key={v.key}>
            {Array.from({ length: end - start }, (_, i) => {
              const itemIndex = start + i;
              const node = validatedItems[itemIndex];
              const isBanner =
                React.isValidElement(node) &&
                node.key != null &&
                String(node.key).startsWith('bottom-banner');
              if (i === 0) {
                const rowIsBanner = end - start === 1 && isBanner;
                if (rowIsBanner) {
                  const bannerProps = {
                    ref: makeRowMeasureRef(rowIndex),
                    'data-index': rowIndex,
                    'data-row': rowIndex,
                    'data-idx': itemIndex,
                    style: { flexBasis: '100%', width: '100%' }
                  } as unknown as React.HTMLAttributes<HTMLDivElement>;
                  return (
                    <div key={itemIndex} {...bannerProps}>
                      {node}
                    </div>
                  );
                }
                const commonProps = {
                  ref: makeRowMeasureRef(rowIndex),
                  'data-index': rowIndex,
                  'data-row': rowIndex,
                  'data-idx': itemIndex
                } as unknown as React.HTMLAttributes<HTMLDivElement>;
                return isBanner ? (
                  <div key={itemIndex} {...commonProps}>
                    {node}
                  </div>
                ) : (
                  <ItemCardWrapperStyled key={itemIndex} {...commonProps}>
                    {node}
                  </ItemCardWrapperStyled>
                );
              }
              const restProps = {
                'data-idx': itemIndex
              } as unknown as React.HTMLAttributes<HTMLDivElement>;
              return isBanner ? (
                <div key={itemIndex} {...restProps}>
                  {node}
                </div>
              ) : (
                <ItemCardWrapperStyled key={itemIndex} {...restProps}>
                  {node}
                </ItemCardWrapperStyled>
              );
            })}
          </React.Fragment>
        );
      })}
      <Loader
        loading={loading && !!validatedItems.length && !isStopLoad}
        optionalClass="endless-loader main-loader--full-width"
      />
      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
    </CardsContainer>
  );
};
